import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormikProps } from 'formik';
import DialogContent from '@mui/material/DialogContent';
import { makeStyles } from 'tss-react/mui';

import { filtersSelectors } from 'app-components/flux/filters';
import { spendingWatchlistSelectors } from 'app-components/flux/spending-watchlist';
import { rootUiActions } from 'app-components/flux/root-ui';
import { getLogger } from 'app-components/utils/core';

import StdDialog from 'components/Dialogs/StdDialog';
import WatchlistForm from 'components/Dialogs/WatchlistModal/WatchlistForm';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

type UpsertWatchlistData = {
  watchlistId?: string;
  name?: string;
  filterType?: 'categories' | 'payees' | 'tags';
  filterItems?: any;
  targetAmount?: number;
  emoji?: string;
};

type LlmWatchlistDialogProps = {
  dialogId: string;
  operation: 'create' | 'update';
  data: UpsertWatchlistData;
  callback?: (result: { success: boolean; watchlistId?: string; error?: string }) => void;
};

const normalizeEmoji = (emoji?: string) => {
  if (!emoji) return '';

  // If it already looks like our stored format (hex or hex-hex), keep as-is.
  if (/^[0-9a-fA-F-]+$/.test(emoji)) {
    return emoji;
  }

  const codePoints: string[] = [];
  for (const char of Array.from(emoji)) {
    const cp = char.codePointAt(0);
    if (cp != null) {
      codePoints.push(cp.toString(16));
    }
  }

  return codePoints.join('-');
};

export const DIALOG_TYPE_LLM_WATCHLIST = 'LLM_WATCHLIST';

const log = getLogger('components/Dialogs/LlmWatchlistDialog');
const useStyles = makeStyles()(() => ({
  root: {
    position: 'relative',
    minHeight: 320,
  },
}));

const typesData = Object.freeze({
  CHARTOFACCOUNT: ['Category', 'Categories', 'categories'],
  PAYEE: ['Payee', 'Payees', 'payeeNames'],
  TAG: ['Tag', 'Tags', 'tags'],
});

const filterKeyObj = {
  CHARTOFACCOUNT: 'chartOfAccounts',
  PAYEE: 'payees',
  TAG: 'tags',
} as const;

const llmFilterTypeToFormType: Record<string, 'CHARTOFACCOUNT' | 'PAYEE' | 'TAG'> = {
  categories: 'CHARTOFACCOUNT',
  payees: 'PAYEE',
  tags: 'TAG',
};

const LlmWatchlistDialog = ({ dialogId, operation, data, callback }: LlmWatchlistDialogProps) => {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  log.debug({ data });

  const isEditing = operation === 'update' && Boolean(data.watchlistId);

  const filters = useAppSelector(filtersSelectors.getFilters);
  const watchlist = useAppSelector((state) =>
    isEditing && data.watchlistId
      ? spendingWatchlistSelectors.getSpendingWatchListById(state as any, data.watchlistId)
      : undefined,
  );

  const existingFilter = useMemo(() => {
    if (!isEditing || !watchlist?.filterId) return null;
    return filters.find((f) => f.id === watchlist.filterId) || null;
  }, [filters, isEditing, watchlist]);

  const formFilterType = useMemo(
    () =>
      isEditing
        ? (() => {
            const t = existingFilter?.filterItems?.[0]?.type;
            return t === 'CHARTOFACCOUNT' || t === 'PAYEE' || t === 'TAG' ? t : null;
          })()
        : data.filterType
          ? llmFilterTypeToFormType[data.filterType]
          : null,
    [isEditing, existingFilter, data.filterType],
  );

  const [formData, setFormData] = useState<any | null>(null);
  const [successModal, setSuccessModal] = useState<'create' | 'update' | null>(null);

  const setFormDataWrapper = useCallback(
    (updatedFormData: FormikProps<UpsertWatchlistData>) => {
      if (
        !formData ||
        formData.dirty !== updatedFormData.dirty ||
        formData.isSubmitting !== updatedFormData.isSubmitting ||
        formData.isValid !== updatedFormData.isValid ||
        formData.isValidating !== updatedFormData.isValidating ||
        formData.resetForm !== updatedFormData.resetForm ||
        formData.submitForm !== updatedFormData.submitForm ||
        formData.values !== updatedFormData.values
      ) {
        setFormData(updatedFormData);
      }
    },
    [formData],
  );

  const overwriteInitialData = useMemo(() => {
    if (isEditing || !formFilterType) return undefined;
    const key = filterKeyObj[formFilterType];
    if (!key) return undefined;

    const rawItems = (data as any).filterItems ?? [];

    // WEB-ONLY HACK: tool payload uses COA type (EXPENSE/INCOME/etc),
    // but this form expects CATEGORY for category watchlists.
    const normalizedItems =
      formFilterType === 'CHARTOFACCOUNT'
        ? Array.isArray(rawItems)
          ? rawItems.map((item: any) => (item && typeof item === 'object' ? { ...item, type: 'CATEGORY' } : item))
          : rawItems
        : rawItems;

    return {
      name: data.name ?? '',
      emoji: normalizeEmoji(data.emoji) ?? '',
      targetAmount: data.targetAmount,
      showTargetAmount: Boolean(data.targetAmount != null).toString(), // boolean as a string, kinda odd
      filter: {
        type: formFilterType,
        [key]: normalizedItems,
      },
    };
  }, [isEditing, formFilterType, data]);

  const closeDialog = useCallback(() => {
    dispatch(rootUiActions.removeDialog(dialogId));
  }, [dispatch, dialogId]);

  const handleCancel = useCallback(() => {
    callback?.({ success: false, error: 'User cancelled' });
    closeDialog();
  }, [callback, closeDialog]);

  const memoForm = useMemo(
    () =>
      existingFilter || formFilterType ? (
        <WatchlistForm
          typesData={typesData}
          watchlist={watchlist}
          isEditing={isEditing}
          filter={isEditing ? existingFilter : formFilterType}
          setFormData={setFormDataWrapper}
          overwriteInitialData={overwriteInitialData}
          setSuccessModal={setSuccessModal}
        />
      ) : null,
    [existingFilter, formFilterType, watchlist, isEditing, setFormDataWrapper, overwriteInitialData],
  );

  useEffect(() => {
    if (!successModal) return;

    callback?.({ success: true, watchlistId: data.watchlistId });
    closeDialog();
  }, [callback, closeDialog, data.watchlistId, successModal]);

  return (
    <StdDialog
      onClose={handleCancel}
      open={successModal === null}
      showCloseButton
      size='md'
      title={`${isEditing ? 'Edit' : 'Create a'} Watchlist`}
    >
      <DialogContent className={classes.root}> {memoForm}</DialogContent>
    </StdDialog>
  );
};

export default LlmWatchlistDialog;
